import {AuthService} from '$services/auth/auth.service'
import {IconsService} from '$services/icons/icons.service'
import {PostsService} from '$services/posts/posts.service'
import {ContactRole, HorseCategory, HorseSex, PostDisplay, PostStatus, UpdatePostForm, User} from '$types'
import {MaxSizeValidator} from '@angular-material-components/file-input'
import {Component, Inject, OnInit} from '@angular/core'
import {AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators} from '@angular/forms'
import {MatDialogRef, MAT_DIALOG_DATA} from '@angular/material/dialog'
import {MatSnackBar} from '@angular/material/snack-bar'
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser'
import {TranslateService} from '@ngx-translate/core'
import {concat, Observable, of} from "rxjs";
import {catchError, finalize, tap} from "rxjs/operators";
import {FileInput} from "ngx-material-file-input";

interface DialogData {
    id: string
}

type PossibleFormValue = AbstractControl | null | undefined

@Component({
    selector: 'app-post-update',
    templateUrl: './post-update.component.html',
    styleUrls: ['./post-update.component.css']
})

export class PostUpdateComponent implements OnInit {
    success_message?: string
    failure_message?: string
    post?: PostDisplay
    entityForm!: FormGroup
    files_max_size: number = 2000 // 2 Mo
    user?: User | null
    sanitizedCertificateOfSale: any
    sanitizedCertificationOfThePerson: any
    sanitizedExtractKbis: any
    sanitizedHorseRegistration: any

    constructor(
        private readonly dialogRef: MatDialogRef<any>,
        @Inject(MAT_DIALOG_DATA) public data: DialogData | null,
        private readonly postService: PostsService,
        private readonly snackBar: MatSnackBar,
        private readonly authService: AuthService,
        private readonly formBuilder: FormBuilder,
        private readonly translateService: TranslateService,
        private readonly domSanitizer: DomSanitizer,
        private readonly icons: IconsService) {
    }

    ngOnInit(): void {

        if (!this.data || !this.data.id) {
            throw new Error("no id was provided !")
        }

        this.user = this.authService.user
        this.icons.registerIcon(['file-import'])
        this.getTranslatedMessages()
        this.retrievePost(this.data.id)
    }

    private getTranslatedMessages() {
        this.success_message = this.translateService.instant('success.save')
        this.failure_message = this.translateService.instant('errors.save')
    }

    private retrievePost(id: string): void {
        this.entityForm = this.buildEntityForm()
        this.postService
            .findById(id)
            .subscribe((post) => {
                this.post = post
                this.setValueInForm()
                this.setDisabled()

                //sanitize
                this.sanitizedCertificateOfSale = this.sanitizeUrl(this.post?.certificateOfSale)
                this.sanitizedCertificationOfThePerson = this.sanitizeUrl(this.post?.certificationOfThePerson)
                this.sanitizedExtractKbis = this.sanitizeUrl(this.post?.extractKbis)
                this.sanitizedHorseRegistration = this.sanitizeUrl(this.post?.horseRegistration)

            })
    }

    private sanitizeUrl(url: string | undefined): SafeResourceUrl | undefined {
        return url ?
            this.domSanitizer.bypassSecurityTrustResourceUrl(url) :
            undefined
    }

    private buildEntityForm(): FormGroup<any> {
        const form: FormGroup = this.formBuilder.group({
            sire: [
                this.post?.horse?.sire || '',
                Validators.pattern(/^(\d+[A-Za-z])?$/)
            ],
            horse_informations: this.buildHorseForm(),
            contact_informations: this.buildContactForm(),
            documents: this.buildDocumentForm(),
        })

        return form
    }

    setValueInForm() {

        const settedValues = {
            sire: this.post?.horse?.sire,
            horse_informations: this.setHorseValue(),
            contact_informations: this.setContactValue(),
            documents: this.setDocumentsValue()
        }
        this.entityForm.setValue(settedValues)
    }

    private setDocumentsValue(): any {
        return {
            certificate_of_sale: this.post?.certificateOfSale || null,
            certification_of_the_person: this.post?.certificationOfThePerson || null,
            extract_kbis: this.post?.extractKbis || null,
            horse_registration: this.post?.horseRegistration || null
        }
    }

    private setHorseValue(): any {
        return {
            horse_name: this.post?.horse?.name || '',
            horse_year: this.post?.horse?.birthYear || 2010,
            horse_sex: this.post?.horse?.sex.toLowerCase() || 'female',
            horse_total_earn: this.post?.horse?.totalEarn || 0,
            horse_record: this.post?.horse?.raceRecord || '',
            horse_father: this.post?.horse?.father || '',
            horse_mother: this.post?.horse?.mother || '',
            category: this.post?.category.toLowerCase() || '',
            horse_location: this.post?.horse?.location || '',
            horse_photos: this.post?.horse?.photos || [],
            sell_price: this.post?.sellPrice || 0,
            to_negociate: this.post?.toNegociate || false,
            taxable_vat: this.post?.taxableVat || false,
            comments_fr: this.post?.commentsFr || '',
            comments_en: this.post?.commentsEn || '',
        }
    }

    private setContactValue(): any {
        return {
            last_name: this.post?.contact?.lastName || '',
            first_name: this.post?.contact?.firstName || '',
            role: this.post?.contact?.role.toLowerCase() || '',
            landline_phone_number: this.post?.contact?.landlinePhoneNumber || '',
            mobile_phone_number: this.post?.contact?.mobilePhoneNumber || '',
            email: this.post?.contact?.email || '',
            is_english: this.post?.contact?.isEnglish || false,
            mail_confirmation: this.post?.mailConfirmation || false,
        }
    }

    setDisabled() {
        this.entityForm.get('sire')?.disable()
        this.getInHorse('horse_name')?.disable()
        this.getInHorse('horse_sex')?.disable()
        this.getInHorse('horse_year')?.disable()
        this.getInHorse('horse_total_earn')?.disable()
        this.getInHorse('horse_record')?.disable()
        this.getInHorse('horse_father')?.disable()
        this.getInHorse('horse_mother')?.disable()
    }

    buildDocumentForm() {
        return this.formBuilder.group({
            certificate_of_sale: [
                this.post?.certificateOfSale,
                MaxSizeValidator(this.files_max_size * 1024),
            ],
            certification_of_the_person: [
                this.post?.certificationOfThePerson,
                MaxSizeValidator(this.files_max_size * 1024),
            ],
            extract_kbis: [
                this.post?.extractKbis,
                MaxSizeValidator(this.files_max_size * 1024),
            ],
            horse_registration: [
                this.post?.horseRegistration,
                [Validators.required, MaxSizeValidator(this.files_max_size * 1024)],
            ],
        })
    }

    private buildContactForm() {
        return this.formBuilder.group({
            last_name: this.required(this.post?.contact?.lastName || ''),
            first_name: this.required(this.post?.contact?.firstName || ''),
            role: this.required(this.post?.contact?.role.toLowerCase() || ''),
            mobile_phone_number: this.required(this.post?.contact?.mobilePhoneNumber || ''),
            landline_phone_number: [
                this.post?.contact?.landlinePhoneNumber || '',
            ],
            email: [
                this.post?.contact?.email || '',
                [Validators.required, Validators.email],
            ],
            is_english: [this.post?.contact?.isEnglish || false],
            mail_confirmation: [this.post?.mailConfirmation || false],
        })
    }

    private required(value: any) {
        return [value, {required: true}]
    }

    private buildHorseForm() {
        return this.formBuilder.group({
            horse_name: this.required(this.post?.horse?.name || ''),
            horse_year: this.required(this.post?.horse?.birthYear || 2010),
            horse_sex: this.required(this.post?.horse?.sex.toLowerCase() || 'female'),
            horse_total_earn: this.required(this.post?.horse?.totalEarn || 0),
            horse_record: this.required(this.post?.horse?.raceRecord || ''),
            horse_father: this.required(this.post?.horse?.father || ''),
            horse_mother: this.required(this.post?.horse?.mother || ''),
            category: this.required(this.post?.category.toLowerCase() || ''),
            horse_location: this.required(this.post?.horse?.location || ''),
            horse_photos: this.required(this.post?.horse?.photos || []),
            sell_price: this.required(this.post?.sellPrice || 0),
            to_negociate: [this.post?.toNegociate || false],
            taxable_vat: [this.post?.taxableVat || false],
            comments_fr: [this.post?.commentsFr || ''],
            comments_en: [this.post?.commentsEn || ''],
        })
    }

    protected getInHorse(name: string): PossibleFormValue {
        return this.entityForm.get('horse_informations')?.get(name)
    }

    protected getInDocuments(name: string): PossibleFormValue {
        return this.entityForm.get('documents')?.get(name)
    }

    protected getInContact(name: string): PossibleFormValue {
        return this.entityForm.get('contact_informations')?.get(name)
    }

    protected get entityUpdate(): UpdatePostForm | undefined {
        if (!this.post) {
            return
        }

        if ((!this.post.horse)) {
            return
        }

        return {
            id: this.post.id,
            idClient: this.user?.id || 'x0',
            category: this.getInHorse('category')?.value.toUpperCase() || HorseCategory.YEARLING,
            sellPrice: this.getInHorse('sell_price')?.value,
            commentsEn: this.getInHorse('comments_en')?.value,
            commentsFr: this.getInHorse('comments_fr')?.value,
            toNegociate: this.getInHorse('to_negociate')?.value,
            taxableVat: this.getInHorse('taxable_vat')?.value,
            mailConfirmation: this.getInContact('mail_confirmation')?.value,
            status: PostStatus.WAITING_VALIDATION,
            contact: {
                firstName: this.getInContact('first_name')?.value,
                lastName: this.getInContact('last_name')?.value,
                role: this.getInContact('role')?.value.toUpperCase() || ContactRole.UNIQUE_OWNER,
                landlinePhoneNumber: this.getInContact('landline_phone_number')?.value,
                mobilePhoneNumber: this.getInContact('mobile_phone_number')?.value,
                email: this.getInContact('email')?.value,
                isEnglish: this.getInContact('is_english')?.value,
            },
            horse: {
                id: this.post.horse.id,
                name: this.getInHorse('horse_name')?.value,
                birthYear: this.getInHorse('horse_year')?.value,
                sex: this.getInHorse('horse_sex')?.value.toUpperCase() || HorseSex.MALE.toUpperCase(),
                raceRecord: this.getInHorse('horse_record')?.value,
                totalEarn: this.getInHorse('horse_total_earn')?.value,
                mother: this.getInHorse('horse_mother')?.value,
                father: this.getInHorse('horse_father')?.value,
                location: this.getInHorse('horse_location')?.value,
                photos: this.getInHorse('horse_photos')?.value,
                mailConfirmation: this.getInContact('mail_confirmation')?.value,
                sire: this.post.horse.sire
            },
            photos: this.getInHorse('horse_photos')?.value,
            certificateOfSale:
                this.getInDocuments('certificate_of_sale')?.value ||
                this.post?.certificateOfSale,
            certificationOfThePerson:
                this.getInDocuments('documents_certification_of_the_person')?.value ||
                this.post?.certificationOfThePerson,
            extractKbis:
                this.getInDocuments('documents_extract_kbis')?.value ||
                this.post?.extractKbis,
            horseRegistration:
                this.getInDocuments('documents_horse_registration')?.value ||
                this.post?.horseRegistration
        }
    }

    protected isAgent(): boolean {
        return this.post?.contact?.role === ContactRole.AGENT
    }

    protected isCoowner(): boolean {
        return this.post?.contact?.role === ContactRole.COOWNER
    }

    getUploadErrors(name: string): ValidationErrors {
        return this.getInDocuments('documents_horse_registration')?.value?.errors ? this.getInDocuments('documents_horse_registration')?.value?.errors : {}
    }

    cancel(): void {
        this.dialogRef.close()
    }

    save(): void {

        if (!this.entityUpdate || !this.post) {
            return
        }
        this.postService
            .update(this.entityUpdate, this.post)
            .subscribe((post) => {
                let photos = this.getInHorse('horse_photos')?.value;
                if (photos && photos.length !== 0) {
                    const isValidPhotos = Array.isArray(this.entityUpdate?.photos?.files) && this.entityUpdate?.photos?.files.every((photo: File) => {
                        const isJpg = photo.type === 'image/jpeg';
                        const isSizeValid = photo.size <= 5 * 1024 * 1024; // 5 Mo en octets
                        return isJpg && isSizeValid;
                    });
                    if (!isValidPhotos) {
                        let messages = 'Certaines photos ne sont pas valides. Veuillez varifier que les photos sont de type JPEG et de taille maximale de 5mo par photo.';
                        console.error('Certaines photos ne sont pas valides. Veuillez varifier que les photos sont de type JPEG et de taille maximale de 5mo par photo.');
                        this.snackBar.open(messages, 'Ok', {
                            duration: 5000,
                            verticalPosition: 'top',
                            panelClass: 'bg-red',
                        })
                        this.dialogRef.close()
                        return;
                    }
                    if (this.entityUpdate?.id && this.entityUpdate?.photos && this.entityUpdate.photos.files) {
                        this.uploadPhotos(this.entityUpdate?.id.toString(), this.entityUpdate?.photos?.files).subscribe(response => {
                           this.toast(post);
                        });
                    }
                }else{
                    this.toast(post);
                }
            });
    }

    toast(post:any){
        const message = post ?
                this.translateService.instant('success.save') :
                this.translateService.instant('errors.save'),
            background_color = post ? 'bg-green' : 'bg-red'
        this.snackBar.open(message, 'Ok', {
            duration: 5000,
            verticalPosition: 'top',
            panelClass: [background_color],
        });
        this.dialogRef.close();
    }

    uploadPhotos(postId: string, horsePhotos: File[]): Observable<any> {
        const observables: Observable<any>[] = horsePhotos.map((photo: File) => {
            return this.postService.addPhotoToPost(postId, photo).pipe(
                catchError(error => {
                    console.error('Error adding photo', error);
                    return of(error);
                }),
                tap(updated => {
                    this.postService.replacePost(updated);
                })
            );
        });
        return concat(...observables);
    }
}
